import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, Card, CardContent, Button, Chip } from '@mui/material';
import { ChevronLeft, ChevronRight, VisibilityOutlined, PlayArrow, PictureAsPdfOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchActiveProducts } from '../../store/productsSlice';


const Shop = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { user } = useSelector((state) => state.auth);
  const { items: products, loading } = useSelector((state) => state.products);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!products || products.length === 0) {
      dispatch(fetchActiveProducts());
    }
  }, [dispatch, products]);

  // Limiter à 8 produits pour l'affichage initial
  const displayedProducts = products.slice(0, 8);

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? displayedProducts.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === displayedProducts.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const handleItemClick = (product) => {
    if (!user) {
      toast.info(
        <div>
          <p><strong>Vous devez vous connecter</strong></p>
          <p>Connectez-vous pour accéder à la boutique baoubab</p>
        </div>,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          style: { color: '#333', background: '#ffffff', fontFamily: 'Roboto, sans-serif' }
        }
      );
      return;
    }
    
    navigate(`/article/${product.id}`);
  };

  const handleSeeMore = () => {
    if (!user) {
      toast.info('Connectez-vous pour voir tous les articles', {
        position: "top-center",
        autoClose: 3000
      });
      return;
    }
    navigate('/articles');
  };

  const getVisibleItems = () => {
    if (displayedProducts.length === 0) return [];
    
    const items = [];
    // Affiche jusqu'à 4 produits visibles
    for (let i = 0; i < Math.min(4, displayedProducts.length); i++) {
      const index = (currentIndex + i) % displayedProducts.length;
      items.push(displayedProducts[index]);
    }
    return items;
  };

  const visibleItems = getVisibleItems();

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Chargement des produits...
        </Typography>
      </Box>
    );
  }

  if (displayedProducts.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Aucun produit disponible
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: { xs: '90%', lg: '100%' },
        maxWidth: { xs: '800px', lg: '1300px' },
        py: 4,
        mx: 'auto'
      }}
    >
      {/* En-tête avec titre */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
        
      }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            color: '#784e3c',
            fontFamily: 'Achiko',
            fontSize:'14px',
          }}
        >
 Découvrez les  solutions !
        </Typography>
        
       <Button
            onClick={handleSeeMore}
          startIcon={<VisibilityOutlined />}
          sx={{
            backgroundColor: '#FFD88C',
            color: '#000000',
            fontWeight: 'bold',
            textTransform: 'none',
            borderRadius: '25px',
            px: 3,
            py: 1,
            boxShadow: '0px 4px 10px rgba(255, 216, 140, 0.4)',
            fontFamily: 'Achiko',
            fontSize:'10px',
            '&:hover': {
              backgroundColor: '#FFC870',
              boxShadow: '0px 6px 15px rgba(255, 216, 140, 0.6)',
              transform: 'translateY(-2px)',
            }
          }}
        >
         Voir plus
        </Button>
      </Box>

      {/* Carousel Container */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          overflowX: 'auto',
          justifyContent: 'center',
          pb: 2,
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: '#a8a8a8',
            },
          },
        }}
      >
        {visibleItems.map((item, index) => (
          <Card
            key={index}
            sx={{
              width: { xs: '220px', md: '240px', lg: '260px' },
              flex: '0 0 auto',
              borderRadius: '20px',
              boxShadow: '0px 4px 15px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0px 8px 25px rgba(0,0,0,0.15)',
              }
            }}
            onClick={() => handleItemClick(item)}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box sx={{ position: 'relative', mb: 1 }}>
                <img
                  src={item.image_url || 'https://via.placeholder.com/150x150?text=Produit'}
                  alt={item.title}
                  style={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '10px'
                  }}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/150x150?text=Image+non+disponible';
                  }}
                />
                
                {/* Badges en haut à droite */}
                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                  {item.is_free && (
                    <Chip
                      label="GRATUIT"
                      size="small"
                      sx={{
                        bgcolor: '#4CAF50',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.6rem'
                      }}
                    />
                  )}
                  {!item.is_free && item.product_type === 'virtual' && (
                    <Chip
                      label="PREMIUM"
                      size="small"
                      sx={{
                        bgcolor: '#FF9800',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.6rem'
                      }}
                    />
                  )}
                </Box>

                {/* Badges en haut à gauche */}
                <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Chip
                    label={item.product_type === 'physical' ? ' Physique' : ' Virtuel'}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.9)',
                      fontSize: '0.6rem'
                    }}
                  />
                  {/* Badge type de contenu virtuel */}
                  {item.product_type === 'virtual' && item.virtual_type && (
                    <Chip
                      icon={item.virtual_type === 'video' ? <PlayArrow sx={{ fontSize: '0.7rem' }} /> : <PictureAsPdfOutlined sx={{ fontSize: '0.7rem' }} />}
                      label={item.virtual_type === 'video' ? 'VIDÉO' : 'PDF'}
                      size="small"
                      sx={{
                        bgcolor: item.virtual_type === 'video' ? '#FF5722' : '#2196F3',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.6rem'
                      }}
                    />
                  )}
                </Box>
              </Box>
              
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: '#333',
                  mb: 1,
                  fontFamily: 'Achiko',
                  fontSize: '0.95rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {item.title}
              </Typography>
              
              {/* Prix seulement pour les produits physiques */}
              {item.product_type === 'physical' && (
                <Typography
                  variant="body2"
                  sx={{
                    color: item.is_free ? '#4CAF50' : '#784e3c',
                    fontWeight: 'bold',
                    bgcolor: item.is_free ? 'rgba(76, 175, 80, 0.1)' : 'rgba(67, 172, 217, 0.1)',
                    borderRadius: '15px',
                    py: 0.5,
                    px: 2,
                    display: 'inline-block'
                  }}
                >
                  {item.is_free ? 'Gratuit' : `${item.price} FCFA`}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}

      </Box>
      
      {/* Boutons de navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
        <IconButton
          onClick={handlePrevious}
          sx={{
            backgroundColor: 'rgba(67, 172, 217, 0.1)',
            color: '#784e3c',
            '&:hover': {
              backgroundColor: 'rgba(67, 172, 217, 0.2)',
            }
          }}
        >
          <ChevronLeft />
        </IconButton>
        
        <IconButton
          onClick={handleNext}
          sx={{
            backgroundColor: 'rgba(67, 172, 217, 0.1)',
            color: '#784e3c',
            '&:hover': {
              backgroundColor: 'rgba(67, 172, 217, 0.2)',
            }
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>
      
      {/* Indicateurs */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mt: 2
        }}
      >
        {displayedProducts.map((_, index) => (
          <Box
            key={index}
            onClick={() => setCurrentIndex(index)}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: index === currentIndex ? '#784e3c' : 'rgba(67, 172, 217, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(67, 172, 217, 0.6)',
              }
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default Shop;